import { Modal, ModalContent } from '@grupoboticario/flora-react'
import { AudioIcon, AudioOffIcon } from '@grupoboticario/flora-react-icons'
import { useMemo, useState } from 'react'
import { VideoShowcase } from 'src/@interfaces'
import VideoPlayer from '../../atoms/VideoPlayer/VideoPlayer'
import { WithTheme } from '../BrandThemeProvider/WithTheme'
import { VideoProductCard, VideoProductCardProps } from './VideoProductCard'
import { AudioControllerButton, ModalCloseButton, PlayerContainer } from './VideoScrollArea.styles'
import { VideoTimeline } from './VideoTimeline'

export type VideoModalProps = {
  video: VideoShowcase
  closeModal: () => void
  onMuted: (muted: boolean, currentVideo: VideoShowcase) => void
} & Pick<VideoProductCardProps, 'onClickProductDetails'>

export const VideoModal = ({
  video,
  closeModal,
  onMuted,
  onClickProductDetails,
}: VideoModalProps) => {
  const [videoDuration, setVideoDuration] = useState(0)
  const [videoProgress, setVideoProgress] = useState(0)
  const [muted, setMuted] = useState(true)

  const onVideoProgress = (playedSeconds: number) => {
    setVideoProgress(playedSeconds / videoDuration)
  }

  const onVideoDuration = (duration: number) => {
    setVideoDuration(duration)
  }

  const videoSize = useMemo(() => {
    const viewportHeight = window.innerHeight
    const viewportWidth = window.innerWidth

    // if viewport width is greater than height, we shouldn't calculate the new width, because it's either on horizontal orientation or it's a desktop
    if (viewportWidth > viewportHeight) {
      return { width: 100, margin: 0 }
    }

    const viewportAspectRatio = viewportHeight / viewportWidth
    // 16:9 is the standard aspect ratio for shorts
    const shortsAspectRatio = 16 / 9

    const screenVideoRatio = viewportAspectRatio / shortsAspectRatio
    const marginSize = ((screenVideoRatio - 1) / 2) * 100

    return {
      width: screenVideoRatio * 100,
      margin: marginSize,
    }
  }, [])

  const toggleMuted = () => {
    const newMutedValue = !muted
    setMuted(newMutedValue)
    onMuted(newMutedValue, video)
  }

  return (
    <Modal
      open
      onOpenChange={closeModal}
    >
      <ModalContent isFull>
        <PlayerContainer
          css={{
            '.react-player': { margin: `0 -${videoSize.margin}%` },
          }}
        >
          <VideoPlayer
            url={video.url}
            width={`${videoSize.width}%`}
            height="100%"
            controls={false}
            muted={muted}
            playing
            loop
            progressInterval={500}
            onDuration={onVideoDuration}
            onProgress={({ playedSeconds }) => onVideoProgress(playedSeconds)}
            class="react-player"
            config={{
              youtube: {
                playerVars: { controls: 0, modestbranding: 0 },
              },
            }}
          />
        </PlayerContainer>

        <WithTheme>
          <VideoTimeline videoProgress={videoProgress} />

          <ModalCloseButton />
          <AudioControllerButton
            aria-label={muted ? 'Desmutar vídeo' : 'Mutar vídeo'}
            onClick={toggleMuted}
            icon={muted ? <AudioOffIcon /> : <AudioIcon />}
            has="iconOnly"
            hierarchy="secondary"
            surfaceColor="dark"
            size="small"
          />
          <VideoProductCard
            productSku={video.sku}
            onClickProductDetails={onClickProductDetails}
          />
        </WithTheme>
      </ModalContent>
    </Modal>
  )
}
